import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { defaultFormValues, validateForm, toLactateTestInput, LactateFormValues } from '../formModel';
import { LactateClient, Modality, ReportType } from '../types';
import { MetaSection } from './sections/MetaSection';
import { ThresholdSection } from './sections/ThresholdSection';
import { ZoneSection } from './sections/ZoneSection';
import { Vo2Section } from './sections/Vo2Section';
import { ChartUpload } from './sections/ChartUpload';
import { GenerateModal } from './GenerateModal';
import { SectionCard } from './SectionCard';
import { useSubmitLactateTest } from './useSubmitLactateTest';
import { loadDraft, saveDraft, clearDraft } from '../utils/draftStorage';
import { firstErrorField } from '../utils/firstErrorField';

const todayISO = () => new Date().toISOString().slice(0, 10);

const MODALITY_OPTIONS: { value: Modality; label: string }[] = [
  { value: 'treadmill', label: 'Treadmill' },
  { value: 'bike_erg', label: 'Bike-Erg' },
  { value: 'row_erg', label: 'Row-Erg' },
];

const REPORT_TYPE_HELP: Record<ReportType, string> = {
  Initial: 'First baseline test for this client and modality.',
  Review: "Compares against the client's most recent Initial of the same modality.",
};

const focusField = (name: string | null) => {
  if (!name) return;
  const el = document.querySelector<HTMLElement>(`[name="${name}"]`);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus({ preventScroll: true });
  }
};

export const LactateTestForm: React.FC<{ client: LactateClient; createdBy: string }> = ({
  client,
  createdBy,
}) => {
  const defaults = useMemo(() => {
    const base = defaultFormValues(
      {
        client_id: client.client_id,
        client_name: client.client_name,
        primary_location: client.primary_location,
      },
      todayISO()
    );
    // Restore an in-progress draft for this client, if one exists.
    const draft = loadDraft(client.client_id);
    return draft ? { ...base, ...draft } : base;
  }, [client]);

  const { control, handleSubmit, watch, getValues } = useForm<LactateFormValues>({
    defaultValues: defaults,
  });
  const modality = watch('modality') as Modality;
  const reportType = watch('report_type') as ReportType;
  const vo2Included = watch('vo2_included');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [file, setFile] = useState<File | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { state, submit, reset } = useSubmitLactateTest();

  const errorCount = Object.keys(errors).length;

  // Persist a draft on every change so an interruption never loses entered data.
  useEffect(() => {
    const sub = watch((values) => saveDraft(client.client_id, values as LactateFormValues));
    return () => sub.unsubscribe();
  }, [watch, client.client_id]);

  // Once a report generates successfully the draft is no longer needed.
  useEffect(() => {
    if (state.url) clearDraft(client.client_id);
  }, [state.url, client.client_id]);

  const onGenerate = handleSubmit(async () => {
    const values = getValues();
    const validationErrors = validateForm(values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      focusField(firstErrorField(validationErrors));
      return;
    }

    const input = toLactateTestInput(
      values,
      client.client_id,
      client.client_name,
      createdBy,
      client.email
    );
    setModalOpen(true);
    try {
      await submit({ input, clientId: client.client_id, file });
    } catch {
      /* error surfaced via modal state */
    }
  });

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {client.client_name}{' '}
            <Typography component="span" color="text.secondary" sx={{ fontWeight: 400 }}>
              #{client.client_id}
            </Typography>
          </Typography>
          {client.source === 'manual' && (
            <Chip label="New client" size="small" color="primary" variant="outlined" />
          )}
        </Box>
        {client.source === 'manual' && client.email && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {client.email} · not yet in the directory
          </Typography>
        )}
      </Box>

      <Stack spacing={3}>
        <SectionCard>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Test setup
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'flex-start' }}
          >
            <Controller
              name="report_type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  size="small"
                  label="Report type"
                  helperText={REPORT_TYPE_HELP[field.value as ReportType]}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="Initial">Initial</MenuItem>
                  <MenuItem value="Review">Review</MenuItem>
                </TextField>
              )}
            />
            <Controller
              name="modality"
              control={control}
              render={({ field }) => (
                <TextField {...field} select size="small" label="Modality" sx={{ minWidth: 160 }}>
                  {MODALITY_OPTIONS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="vo2_included"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  sx={{ mt: { sm: 0.5 } }}
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputRef={field.ref}
                    />
                  }
                  label="Include VO₂max"
                />
              )}
            />
          </Stack>
        </SectionCard>

        <SectionCard>
          <MetaSection control={control} />
        </SectionCard>

        <SectionCard>
          <ThresholdSection control={control} modality={modality} errors={errors} />
        </SectionCard>

        <SectionCard>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Measured HRmax
          </Typography>
          <Controller
            name="measured_hr_max"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Measured HRmax (bpm)"
                size="small"
                error={Boolean(errors['measured_hr_max'])}
                helperText={errors['measured_hr_max']}
              />
            )}
          />
        </SectionCard>

        <SectionCard>
          <ZoneSection control={control} modality={modality} errors={errors} />
        </SectionCard>

        {vo2Included && (
          <SectionCard>
            <Vo2Section control={control} modality={modality} errors={errors} />
          </SectionCard>
        )}

        <SectionCard>
          <ChartUpload file={file} onFile={setFile} />
        </SectionCard>

        <SectionCard>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Next steps
          </Typography>
          <Controller
            name="next_steps"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Next steps (optional)"
                placeholder="What should the client do with these results? e.g. training focus, retest timing, areas to develop."
                fullWidth
                multiline
                minRows={3}
                // Capped to keep the PDF "Next steps" section within page 1.
                // Mirror this limit in functions viewModel (NEXT_STEPS_MAX_LEN).
                inputProps={{ maxLength: 400 }}
                error={Boolean(errors['next_steps'])}
                helperText={errors['next_steps'] ?? `${(field.value as string)?.length ?? 0}/400`}
              />
            )}
          />
        </SectionCard>
      </Stack>

      <Box sx={{ mt: 4 }}>
        {errorCount > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorCount === 1
              ? 'One field needs attention before you can generate the report.'
              : `${errorCount} fields need attention before you can generate the report.`}
          </Alert>
        )}
        <Button variant="contained" size="large" onClick={onGenerate}>
          Generate report
        </Button>
      </Box>

      <GenerateModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          reset();
        }}
        reportType={reportType}
        isGenerating={state.isGenerating}
        progress={state.progress}
        reportUrl={state.url}
        downloadUrl={state.downloadUrl}
        priorInitialFound={state.priorInitialFound}
        error={state.error}
        onRetry={onGenerate}
      />
    </Box>
  );
};
